// ── selectedBookConsensus — the "My books" consensus (#1287) ─────────
//
// A trader picks the OddsPapi books they trust; this is the de-vigged fair
// value across ONLY those books, for ONE market. Contract, evidence and
// limits: docs/261022Issue1287SelectedBookConsensus.md.
//
// Producer reference: hydracore f72c37b, src/odds/consensusMath.ts and
// fairValues.ts. Producer-frame controls and explicit rejection cases pin
// the copied math, including low-book behavior. Historical fixtures alone
// do not prove parity with every producer case.
//
// Pure: no store, no clock read, no timer. The caller passes one exact
// market bucket (event, family, period, line — the same readers the table
// uses), so books can never mix across lines. Results are in the PROVIDER's
// away/home slots, exactly like `market.consensus`; align them to a row with
// the same swap the wire consensus uses.
//
// This value is display-only. It must never feed order prices, price
// seeding, ROI, alerts, risk or automated decisions, and it never replaces
// the hydra consensus in the shared market store.

import type { HydraOpMarket } from '@/clients/HydraClient';
import { decimalToImpliedPct, OP_ANCHOR_GLOW_MS, OP_FIELD_FRESH_MS } from './opOdds';

/** The books a consensus may use: hydracore's reviewed sportsbook census
 *  (`OP_CONSENSUS_BOOKS`, HYDRA_API rule 6c — "21 books incl. pinnacle").
 *  Fail-closed like the producer's: an exchange (kalshi, polymarket, …) or a
 *  book outside this list never contributes, whatever the trader selects.
 *  Same slugs as GameDetail/oddsBoard.ts's census labels; widening it is a
 *  producer decision, not a display one. */
export const OP_CONSENSUS_ELIGIBLE_BOOKS: ReadonlySet<string> = new Set([
  'pinnacle', 'fanduel', 'draftkings', 'betmgm', 'caesars', 'bet365', 'betrivers',
  'hardrockbet', 'circasports', 'ballybet', 'betparx', 'borgata', 'fourwinds',
  'desertdiamond', 'bookmaker.eu', 'pointsbet.com.au', 'sbobet', 'singbet', '3et',
  '198bet', 'kaiyun',
]);

/** The sharp anchor. It never competes for the field's freshness slots. */
const ANCHOR = 'pinnacle';
/** `CONSENSUS_MIN_BOOKS` — how many field books the freshness selection
 *  aims for. A target, not a gate: fewer still computes. */
export const CONSENSUS_FIELD_TARGET = 5;
/** Sharp arrival weight: 20× a field vote on a new quote, falling in a
 *  straight line to its resting 4× over OP_ANCHOR_GLOW_MS (6 s). */
const ANCHOR_ARRIVAL_WEIGHT = 20;
const ANCHOR_REST_WEIGHT = 4;
/** Field recency: a field book weighs 1 + exp(−age / 700 ms). */
const FIELD_RECENCY_MS = 700;
/** An aged anchor is struck when any side's fair value is further than
 *  max(10% of the field mean, 3¢) from the field mean. In tenths. */
const DRIFT_SHARE = 0.1;
const DRIFT_FLOOR_TENTHS = 30;

/** Why a selected book is not in the number. */
export type SelectedBookExclusion =
  /** Outside the reviewed census, or an exchange. */
  | 'not-eligible'
  /** Listed in the frame's withdrawnBooks (rule 12) — reason rides along. */
  | 'withdrawn'
  /** No quote for this market and no withdrawal entry. */
  | 'no-quote'
  /** A side is missing or out of the decimal-odds domain, or the time is bad. */
  | 'invalid-quote'
  /** A two-way quote in a three-way market: it has no draw to de-vig. */
  | 'no-draw-quote'
  /** The frame carries this book twice; neither copy is trusted. */
  | 'duplicate-quote'
  /** The field already had its freshest books (CONSENSUS_FIELD_TARGET). */
  | 'older-than-selected'
  /** An aged field quote more than 20 cents from the kept field median. */
  | 'stale-field-outlier'
  /** Pinnacle quoted more than 5 s ago AND disagrees with the field. */
  | 'anchor-aged-and-drifted';

export interface SelectedBookStatus {
  bookmaker: string;
  status: 'contributing' | SelectedBookExclusion;
  /** Contributing books only: their vote relative to a rested field book. */
  weight?: number;
  /** The quote's own update time, when it has one. */
  updatedAtMs?: number;
  /** 'withdrawn' only: the provider's reason and the withhold's start. */
  withdrawal?: { reason: string; sinceMs: number };
}

export interface SelectedBookConsensus {
  /** Fair tenths (0–1000) in the PROVIDER's slots; all sides sum to 1000. */
  awayTenths: number;
  homeTenths: number;
  /** Present ⇔ the market is 3-way. */
  drawTenths?: number;
  contributingBooks: number;
  pinnacleAnchored: boolean;
  /** The freshest contributing quote — the value's freshness authority. */
  lastUpdateMs: number;
}

export interface SelectedBookConsensusResult {
  /** Null whenever no honest value exists — never a guess. */
  consensus: SelectedBookConsensus | null;
  /** Why `consensus` is null; null when there is a value. */
  noValue: 'no-selection' | 'no-usable-quote' | null;
  /** One entry per selected book, in selection order. */
  books: SelectedBookStatus[];
}

interface Candidate {
  bookmaker: string;
  updatedAtMs: number;
  ageMs: number;
  /** De-vigged fair, in tenths, unrounded. */
  fair: number[];
}

/** Multiplicative de-vig from the decimal strings — hydra de-vigs the raw
 *  prices, not the rounded per-book `*FairTenths` (those miss one producer
 *  frame by a tenth). Null when any side the market needs is unusable. */
function devig(decimals: (string | undefined)[]): number[] | null {
  const implied: number[] = [];
  for (const d of decimals) {
    const pct = decimalToImpliedPct(d);
    if (pct == null) return null;
    implied.push(pct);
  }
  const sum = implied.reduce((a, b) => a + b, 0);
  if (sum < 100 || sum > (decimals.length === 3 ? 125 : 120)) return null;
  return implied.map((p) => (p / sum) * 1000);
}

/** Integer tenths summing to exactly 1000: floor each side, then hand the
 *  remainder to the largest fractions (ties in slot order: away, home, draw). */
function toTenths(values: number[]): number[] {
  const floors = values.map(Math.floor);
  let left = 1000 - floors.reduce((a, b) => a + b, 0);
  const order = values.map((_, i) => i).sort((a, b) => (values[b] - floors[b]) - (values[a] - floors[a]) || a - b);
  for (const i of order) {
    if (left <= 0) break;
    floors[i] += 1;
    left -= 1;
  }
  return floors;
}

export function selectedBookConsensus(
  market: Pick<HydraOpMarket, 'books' | 'withdrawnBooks' | 'consensus'>,
  selected: readonly string[],
  nowMs: number,
): SelectedBookConsensusResult {
  const picks = [...new Set(selected)];
  if (picks.length === 0) return { consensus: null, noValue: 'no-selection', books: [] };

  // Rule 5: is the market 3-way? Hydra's consensus shape decides when there
  // is one (plus any eligible book's draw): a stray draw on an exchange quote
  // must not refuse every sportsbook when hydra itself says two-way. With no
  // hydra consensus, ANY draw price makes it 3-way — fail closed, so a
  // three-outcome market never gets a two-way number. Either way a book
  // without the draw is then unusable ('no-draw-quote').
  const eligibleDraw = market.books.some((b) => OP_CONSENSUS_ELIGIBLE_BOOKS.has(b.bookmaker) && b.drawDecimal != null);
  const threeWay = market.consensus != null
    ? market.consensus.drawFairProbTenths != null || eligibleDraw
    : market.books.some((b) => b.drawDecimal != null);
  const statuses = new Map<string, SelectedBookStatus>();
  const candidates: Candidate[] = [];
  for (const slug of picks) {
    if (!OP_CONSENSUS_ELIGIBLE_BOOKS.has(slug)) {
      statuses.set(slug, { bookmaker: slug, status: 'not-eligible' });
      continue;
    }
    const quotes = market.books.filter((b) => b.bookmaker === slug);
    if (quotes.length > 1) {
      statuses.set(slug, { bookmaker: slug, status: 'duplicate-quote' });
      continue;
    }
    const quote = quotes[0];
    if (!quote) {
      const w = market.withdrawnBooks?.find((x) => x.bookmaker === slug);
      statuses.set(slug, w
        ? { bookmaker: slug, status: 'withdrawn', withdrawal: { reason: w.reason, sinceMs: w.sinceMs } }
        : { bookmaker: slug, status: 'no-quote' });
      continue;
    }
    const fair = devig(threeWay ? [quote.awayDecimal, quote.homeDecimal, quote.drawDecimal] : [quote.awayDecimal, quote.homeDecimal]);
    if (fair == null || !Number.isFinite(quote.updatedAtMs)) {
      const twoWayOnly = threeWay && quote.drawDecimal == null && Number.isFinite(quote.updatedAtMs)
        && devig([quote.awayDecimal, quote.homeDecimal]) != null;
      statuses.set(slug, { bookmaker: slug, status: twoWayOnly ? 'no-draw-quote' : 'invalid-quote' });
      continue;
    }
    // A quote stamped ahead of our clock is brand new, not negative-aged.
    candidates.push({ bookmaker: slug, updatedAtMs: quote.updatedAtMs, ageMs: Math.max(0, nowMs - quote.updatedAtMs), fair });
  }

  // Field freshness selection: every book inside the fresh window, topped up
  // with the freshest older books until the field reaches its target.
  const field = candidates.filter((c) => c.bookmaker !== ANCHOR)
    .sort((a, b) => a.ageMs - b.ageMs || a.bookmaker.localeCompare(b.bookmaker));
  const fresh = field.filter((c) => c.ageMs <= OP_FIELD_FRESH_MS);
  const keepN = Math.max(fresh.length, Math.min(CONSENSUS_FIELD_TARGET, field.length));
  let kept = field.slice(0, keepN);
  // Match the producer's median guard before evaluating the sharp anchor.
  // Fresh extremes remain eligible; old outliers cannot move their yardstick.
  let isInlier: ((c: Candidate) => boolean) | null = null;
  if (kept.length >= 3) {
    const sorted = kept.map(c => c.fair[0]).sort((a, b) => a - b);
    const mid = sorted.length >> 1;
    const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    const accepts = (c: Candidate) => c.ageMs <= OP_FIELD_FRESH_MS || Math.abs(c.fair[0] - median) <= 200;
    const survivors = kept.filter(accepts);
    if (survivors.length >= 2 && survivors.length < kept.length) {
      isInlier = accepts;
      for (const c of field.slice(keepN)) {
        if (survivors.length >= keepN) break;
        if (accepts(c)) survivors.push(c);
      }
      kept = survivors;
    }
  }
  const included = new Set(kept);
  for (const c of field) {
    if (!included.has(c)) statuses.set(c.bookmaker, {
      bookmaker: c.bookmaker,
      status: isInlier && !isInlier(c) ? 'stale-field-outlier' : 'older-than-selected',
      updatedAtMs: c.updatedAtMs,
    });
  }
  const anchor = candidates.find((c) => c.bookmaker === ANCHOR);

  const books = () => picks.map((slug) => statuses.get(slug)!);
  if (kept.length === 0 && !anchor) {
    return { consensus: null, noValue: 'no-usable-quote', books: books() };
  }

  const sides = (kept[0] ?? anchor!).fair.length;
  const fieldMean = Array.from({ length: sides }, (_, i) => kept.reduce((a, c) => a + c.fair[i], 0) / kept.length);
  const votes: { c: Candidate; weight: number }[] = kept.map((c) => ({ c, weight: 1 + Math.exp(-c.ageMs / FIELD_RECENCY_MS) }));
  let pinnacleAnchored = false;
  if (anchor) {
    const aged = anchor.ageMs > OP_FIELD_FRESH_MS;
    const drifted = anchor.fair.some((p, i) => Math.abs(p - fieldMean[i]) > Math.max(DRIFT_SHARE * fieldMean[i], DRIFT_FLOOR_TENTHS));
    if (kept.length > 0 && aged && drifted) {
      statuses.set(ANCHOR, { bookmaker: ANCHOR, status: 'anchor-aged-and-drifted', updatedAtMs: anchor.updatedAtMs });
    } else {
      const decay = Math.min(anchor.ageMs, OP_ANCHOR_GLOW_MS) / OP_ANCHOR_GLOW_MS;
      votes.push({ c: anchor, weight: ANCHOR_ARRIVAL_WEIGHT - (ANCHOR_ARRIVAL_WEIGHT - ANCHOR_REST_WEIGHT) * decay });
      pinnacleAnchored = true;
    }
  }
  for (const { c, weight } of votes) {
    statuses.set(c.bookmaker, { bookmaker: c.bookmaker, status: 'contributing', weight, updatedAtMs: c.updatedAtMs });
  }

  const total = votes.reduce((a, v) => a + v.weight, 0);
  const tenths = toTenths(Array.from({ length: sides }, (_, i) => votes.reduce((a, v) => a + v.weight * v.c.fair[i], 0) / total));
  return {
    consensus: {
      awayTenths: tenths[0],
      homeTenths: tenths[1],
      ...(sides === 3 ? { drawTenths: tenths[2] } : {}),
      contributingBooks: votes.length,
      pinnacleAnchored,
      lastUpdateMs: Math.max(...votes.map((v) => v.c.updatedAtMs)),
    },
    noValue: null,
    books: books(),
  };
}
